import React, { useEffect } from 'react';
import {
  Path,
  PathValue,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { FormFieldHeader } from '../FormFieldHeader';
import { FileContent, Validator, useFilePicker } from 'use-file-picker';
import { AttachmentInputErrorMessages } from './AttachmentInputErrorMessages';
import {
  AttachmentInputBody,
  PropTypes as BodyProps,
} from './AttachmentInputBody';
import { AttachmentInputFooter } from './AttachmentInputFooter';
import { MimeTypeAsString } from '../../utils/fileMimeTypes';
import { FileTypeValidator } from '../../utils/useFilePicker/fileTypeValidator';
import { VirusScanValidator } from '../../utils/useFilePicker/virusScanValidator';

interface PropTypes<E> {
  allowedFormats: MimeTypeAsString[];
  className?: string;
  controllerContentName: string;
  controllerFileName: string;
  controllerIdName?: string;
  fieldName: string;
  disabled?: boolean;
  isRequired?: boolean;
  headerClassNames?: string;
  maxFileSizeMb: number;
  hideHeader?: boolean;
  hideAllowedFormats?: boolean;
  hideAlert?: boolean;
  getValues: UseFormGetValues<E>;
  setValue: UseFormSetValue<E>;
  formRegister: UseFormRegister<E>;
  Body?: React.ComponentType<BodyProps<E>>;
  validators?: Validator[];
  error?: { message?: string };
  hideMaxSize?: boolean;
  customFooterText?: string;
}

function getContent(filesContent: FileContent[]): string | undefined {
  if (filesContent?.length === 0) {
    return undefined;
  }

  return filesContent[0].content;
}

function handleChange<E>(
  controllerContentName: string,
  controllerFileName: string,
  filesContent: FileContent[],
  setValue: UseFormSetValue<E>,
  controllerIdName: string,
) {
  if (filesContent?.length !== 0) {
    setValue(
      controllerContentName as Path<E>,
      getContent(filesContent) as PathValue<E, Path<E>>,
      { shouldValidate: true, shouldDirty: true },
    );
    setValue(
      controllerFileName as Path<E>,
      filesContent[0].name as PathValue<E, Path<E>>,
      { shouldValidate: true, shouldDirty: true },
    );
    if (controllerIdName != null) {
      setValue(controllerIdName as Path<E>, null);
    }
  }
}

export function SingleAttachmentInputFormField<E>(props: PropTypes<E>) {
  const hideHeader = props.hideHeader ?? false;
  const value = props.getValues(props.controllerFileName as Path<E>);

  const [openFileSelector, { filesContent, errors, clear, loading }] =
    useFilePicker({
      multiple: false,
      readAs: 'DataURL',
      accept: props.allowedFormats,
      maxFileSize: props.maxFileSizeMb,
      readFilesContent: true,
      validators: props.validators ?? [
        new FileTypeValidator(),
        new VirusScanValidator(),
      ],
    });

  useEffect(() => {
    handleChange(
      props.controllerContentName,
      props.controllerFileName,
      filesContent,
      props.setValue,
      props.controllerIdName,
    );
  }, [filesContent]);

  const Body = props.Body != null ? props.Body : AttachmentInputBody;

  const finalErrors = [props.error]
    .concat(errors.map((e) => ({ message: (e as any).reason })))
    .filter(Boolean);

  return (
    <div className={props.className}>
      {!props.hideAlert && (
        <AttachmentInputErrorMessages
          allowedFormats={props.allowedFormats}
          errors={errors}
          maxFileSizeMb={props.maxFileSizeMb}
          maxFiles={1}
        />
      )}
      {!hideHeader && (
        <FormFieldHeader
          fieldName={props.fieldName}
          isRequired={props.isRequired}
          classNames={props.headerClassNames}
        />
      )}
      <Body
        allowedFormats={props.allowedFormats}
        controllerContentName={props.controllerContentName}
        controllerFileName={props.controllerFileName}
        value={value}
        disabled={props.disabled}
        isRequired={props.isRequired}
        clear={clear}
        formRegister={props.formRegister}
        openFileSelector={openFileSelector}
        setValue={props.setValue}
        isLoading={loading}
        errors={errors}
      />
      <AttachmentInputFooter
        errors={finalErrors}
        allowedFormats={props.allowedFormats}
        hideAllowedFormats={props.hideAllowedFormats}
        maxFileSizeMb={props.maxFileSizeMb}
        hideMaxSize={props.hideMaxSize}
        customFooterText={props.customFooterText}
      />
    </div>
  );
}
